import { useState, useCallback } from 'react';
import { authService } from '../services/AuthService';
import { ChangePasswordRequest } from '../types/auth';

interface UseChangePasswordReturn {
  /** Función para cambiar la contraseña */
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  /** Si está en proceso de cambio */
  isLoading: boolean;
  /** Error si ocurre */
  error: string | null;
  /** Si el cambio fue exitoso */
  success: boolean;
  /** Función para limpiar el estado */
  clearState: () => void;
}

/**
 * Hook para manejar el cambio de contraseña del usuario
 */
export const useChangePassword = (): UseChangePasswordReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Cambia la contraseña del usuario
   */
  const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await authService.changePassword(data);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cambiar la contraseña';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Limpia el estado del hook
   */
  const clearState = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    changePassword,
    isLoading,
    error,
    success,
    clearState
  };
};